import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Trash2, Save, FileText, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, openPdf, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const VITALS = [["bp", "BP"], ["pulse", "Pulse"], ["rr", "Resp. rate"], ["temperature", "Temp °F"],
  ["spo2", "SpO2 %"], ["height", "Height cm"], ["weight", "Weight kg"], ["bmi", "BMI"],
  ["pain_score", "Pain score"], ["rbs", "RBS mg/dL"]];

const TEXTS = [["complaints", "Chief complaints"], ["hpi", "History of present illness"],
  ["past_medical", "Past medical history"], ["past_surgical", "Past surgical history"],
  ["family_history", "Family history"], ["drug_history", "Drug history"],
  ["allergy_history", "Allergy history"], ["personal_history", "Personal history"],
  ["menstrual_history", "Menstrual history"], ["obstetric_history", "Obstetric history"],
  ["general_exam", "General examination"], ["systemic_exam", "Systemic examination"],
  ["differential", "Differential diagnosis"], ["investigations", "Investigations advised"],
  ["treatment_plan", "Treatment plan"], ["advice", "Advice"]];

const EMPTY_MED = { name: "", dose: "", morning: true, afternoon: false, night: true,
  duration: "5 days", route: "Oral", food: "After food", instructions: "", refill: "" };

export default function Consultation() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { facilities, facilityId, user } = useApp();
  const isNew = !id || id === "new";
  const [patient, setPatient] = useState(null);
  const [rxId, setRxId] = useState(null);
  const [rxNumber, setRxNumber] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    appointment_id: sp.get("appointment") ? Number(sp.get("appointment")) : null,
    patient_id: sp.get("patient") ? Number(sp.get("patient")) : null,
    vitals: {}, diagnosis: "", icd_code: "", followup_date: "",
    ...Object.fromEntries(TEXTS.map(([k]) => [k, ""])),
  });
  const [meds, setMeds] = useState([{ ...EMPTY_MED }]);

  useEffect(() => {
    (async () => {
      if (!isNew) {
        const { data } = await api.get(`/consultations/${id}`);
        setForm((f) => ({ ...f, ...data, vitals: data.vitals || {} }));
        setMeds(data.medicines?.length ? data.medicines : [{ ...EMPTY_MED }]);
        setRxId(data.prescription_id); setRxNumber(data.prescription_number);
        const p = await api.get(`/patients/${data.patient_id}`);
        setPatient(p.data);
      } else if (form.patient_id) {
        const p = await api.get(`/patients/${form.patient_id}`);
        setPatient(p.data);
      }
    })().catch((e) => toast.error(errMsg(e)));
    /* eslint-disable-next-line */
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setVital = (k, v) => setForm((f) => ({ ...f, vitals: { ...f.vitals, [k]: v } }));
  const setMed = (i, k, v) => setMeds((m) => m.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    if (!form.patient_id) return toast.error("No patient selected");
    if (!form.diagnosis.trim()) return toast.error("Provisional diagnosis is required");
    const target = patient?.facility_id || (facilityId === "all" ? facilities[0]?.id : Number(facilityId));
    setSaving(true);
    try {
      const payload = { ...form, facility_id: target, medicines: meds.filter((m) => m.name.trim()) };
      const { data } = isNew
        ? await api.post("/consultations", payload)
        : await api.put(`/consultations/${id}`, payload);
      setRxId(data.prescription_id);
      setRxNumber(data.prescription_number || rxNumber);
      toast.success(isNew ? "Consultation saved & prescription generated" : "Consultation updated");
      if (isNew) nav(`/consultation/${data.id}`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const sendWhatsApp = async () => {
    try {
      const { data } = await api.post(`/prescriptions/${rxId}/whatsapp`);
      await openPdf(`/prescriptions/${rxId}/pdf`);
      const num = (data.number || "").replace(/\D/g, "");
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(data.message)}`, "_blank");
      toast.success("PDF opened — attach it in the WhatsApp chat window");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const canEdit = user.role === "super_admin" || user.role === "doctor";
  if (!canEdit) return <p className="text-sm text-destructive">Your role cannot create or edit prescriptions.</p>;

  return (
    <div className="space-y-5" data-testid="consultation-page">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" /> Back
      </Button>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">
            {rxNumber ? `Rx ${rxNumber}` : "New encounter"}
          </p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">
            {patient ? patient.name : "Consultation"}
          </h1>
          {patient && (
            <p className="text-sm text-muted-foreground">
              {patient.uhid} · {patient.age} yrs · {patient.gender}
              {patient.allergies ? ` · Allergies: ${patient.allergies}` : ""}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {rxId && (
            <>
              <Button variant="outline" className="gap-2" data-testid="print-prescription-button"
                onClick={() => openPdf(`/prescriptions/${rxId}/pdf`)}>
                <FileText className="h-4 w-4" /> Prescription PDF
              </Button>
              <Button variant="outline" className="gap-2" data-testid="whatsapp-prescription-button"
                onClick={sendWhatsApp}>Send on WhatsApp</Button>
            </>
          )}
          <Button onClick={save} disabled={saving} className="gap-2" data-testid="save-consultation-button">
            <Save className="h-4 w-4" /> {isNew ? "Save consultation" : "Update"}
          </Button>
        </div>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Vitals</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {VITALS.map(([k, label]) => (
            <div key={k} className="space-y-1.5">
              <Label className="text-xs">{label}</Label>
              <Input value={form.vitals[k] || ""} onChange={(e) => setVital(k, e.target.value)}
                data-testid={`vital-${k}-input`} />
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Diagnosis</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Provisional diagnosis *</Label>
              <Input value={form.diagnosis} onChange={(e) => set("diagnosis", e.target.value)}
                data-testid="diagnosis-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">ICD code</Label>
                <Input value={form.icd_code} onChange={(e) => set("icd_code", e.target.value)}
                  data-testid="icd-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Follow up date</Label>
                <Input type="date" value={form.followup_date} onChange={(e) => set("followup_date", e.target.value)}
                  data-testid="followup-input" /></div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">History & examination</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto">
            {TEXTS.map(([k, label]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs">{label}</Label>
                <Textarea rows={2} value={form[k] || ""} onChange={(e) => set(k, e.target.value)}
                  data-testid={`${k}-input`} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card className="shadow-sm">
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-sm">Prescription (Rx)</CardTitle>
          <Button size="sm" variant="outline" className="gap-1.5" data-testid="add-medicine-button"
            onClick={() => setMeds([...meds, { ...EMPTY_MED }])}>
            <Plus className="h-3.5 w-3.5" /> Add medicine
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {meds.map((m, i) => (
            <div key={i} className="grid gap-3 lg:grid-cols-12 items-end border-b border-border pb-3"
              data-testid={`medicine-row-${i}`}>
              <div className="lg:col-span-3 space-y-1.5"><Label className="text-xs">Medicine</Label>
                <Input value={m.name} onChange={(e) => setMed(i, "name", e.target.value)}
                  data-testid={`medicine-name-${i}`} /></div>
              <div className="lg:col-span-1 space-y-1.5"><Label className="text-xs">Dose</Label>
                <Input value={m.dose} onChange={(e) => setMed(i, "dose", e.target.value)}
                  data-testid={`medicine-dose-${i}`} /></div>
              <div className="lg:col-span-2 space-y-1.5">
                <Label className="text-xs">M / A / N</Label>
                <div className="flex items-center gap-3 h-9">
                  {["morning", "afternoon", "night"].map((t) => (
                    <Checkbox key={t} checked={!!m[t]} onCheckedChange={(v) => setMed(i, t, !!v)}
                      data-testid={`medicine-${t}-${i}`} />
                  ))}
                </div>
              </div>
              <div className="lg:col-span-1 space-y-1.5"><Label className="text-xs">Duration</Label>
                <Input value={m.duration} onChange={(e) => setMed(i, "duration", e.target.value)} /></div>
              <div className="lg:col-span-1 space-y-1.5"><Label className="text-xs">Route</Label>
                <Select value={m.route} onValueChange={(v) => setMed(i, "route", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Oral", "IV", "IM", "SC", "Topical", "Inhalation"].map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
              <div className="lg:col-span-1 space-y-1.5"><Label className="text-xs">Food</Label>
                <Select value={m.food} onValueChange={(v) => setMed(i, "food", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Before food", "After food", "With food", "Empty stomach"].map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
              <div className="lg:col-span-2 space-y-1.5"><Label className="text-xs">Instructions</Label>
                <Input value={m.instructions} onChange={(e) => setMed(i, "instructions", e.target.value)} /></div>
              <Button size="icon" variant="ghost" data-testid={`remove-medicine-${i}`}
                onClick={() => setMeds(meds.filter((_, j) => j !== i))}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
